import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { AppFrame } from "../../components/AppFrame";
import { Tabs } from "../../components/ui/Tabs";
import { ukToday } from "../../lib/field-api";
import { useSocket } from "../../lib/socket";
import type { User } from "../../types";

/**
 * Field Sales: a rep's own front page.
 *
 * No sidebar. It is two tabs today -- Home and Quick Notes -- and a column of
 * two links beside a page with its own tab bar would be the same navigation
 * twice. More tabs are expected (accounts gone quiet, open quotes), and they
 * go in the bar, not in a sidebar that appears when there are enough of them.
 *
 * The tab is a route rather than component state, so the back button works
 * and Home's cards can link straight to today's note; which one was last used
 * is remembered on the device, so opening the rail tile lands where the rep
 * left off.
 */
export function FieldShell({ user }: { user: User }) {
  const { connected } = useSocket();
  return <AppFrame user={user} title="Field Sales" connected={connected} />;
}

export type FieldTab = "home" | "notes";

function tabKey(userId: number): string {
  return `pulse.field.tab.${userId}`;
}

/** `/field` itself: wherever this person was last, Home the first time. */
export function FieldIndex({ user }: { user: User }) {
  let last: string | null = null;
  try {
    last = window.localStorage.getItem(tabKey(user.id));
  } catch {
    /* Blocked storage: Home, which is the default anyway. */
  }
  return <Navigate to={last === "notes" ? "/field/notes" : "/field/home"} replace />;
}

/**
 * The bar across the top of every Field Sales page.
 *
 * Rendered by each page rather than by the shell so a page can sit its own
 * controls beside it, and so the notes page on a phone -- where the open note
 * is a screen of its own -- can leave it out.
 */
export function FieldTabs({ user, tab }: { user: User; tab: FieldTab }) {
  const navigate = useNavigate();

  useEffect(() => {
    try {
      window.localStorage.setItem(tabKey(user.id), tab);
    } catch {
      /* Not worth telling anybody about. */
    }
  }, [tab, user.id]);

  return (
    <Tabs
      label="Field Sales"
      className="px-3"
      value={tab}
      tabs={[
        { value: "home", label: "Home" },
        { value: "notes", label: "Quick Notes" },
      ]}
      onChange={(next) =>
        navigate(next === "home" ? "/field/home" : `/field/notes/${ukToday()}`)
      }
    />
  );
}
